import { messageProxy } from '@jscadui/postmessage'

import { toFSEntry, entryCheckPromise } from './src/FSEntry.js'
import { readAsArrayBuffer, readAsText } from './src/FileReader.js'
import { safariGetAsHandle } from './src/safariFileHandles.js'

/**
 * @typedef {import('./src/FSEntry.js').FSEntry} FSEntry
 * @typedef {import('./src/FSEntry.js').FSDirectoryEntry} FSDirectoryEntry
 * @typedef {import('./src/FSEntry.js').FSFileEntry} FSFileEntry
 *
 * @typedef SwHandler
 * @prop {string} id
 * @prop {string} [fileToRun]
 * @prop {string} folderName
 * @prop {string} projectName
 * @prop {string} defProjectName
 * @prop {Array<FSFileEntry>} filesToCheck
 * @prop {Array<Array<FSEntry>>} roots
 * @prop {number} lastCheck
 * @prop {string} base
 * @prop {unknown} api
 * @prop {Cache} cache
 * @prop {OnFilesChangeHandler} onfileschange
 * @prop {(path:string)=>Promise<FSFileEntry | undefined>} getFile
 * @prop {PickEntry} [pickEntry] the entry rule fileDropped was given
 * @prop {Array<string>} [entryNames] the dropped top-level file names it picks from
 * @prop {string} [entryFolder] the dropped folder's name
 *
 * @callback PickEntry
 * @param {Array<string>} names
 * @param {{folder?: string, packageJson?: string}} context
 * @returns {string | null | undefined}
 *
 * @callback OnFilesChangeHandler
 * @param {Array<string>} files
 * @returns {void}
 * 
 * @typedef WorkspaceAlias
 * @prop {string} name
 * @prop {string} path
 *
 * 
 * @typedef PathInfo
 * @prop {string} url
 * @prop {string} filename
 * @prop {string} ext
 */

export * from './src/FileReader.js'
export * from './src/FSEntry.js'

/**
 * @param {string | Array<string>} path
 * @returns {Array<string>}
 */
/**
 * Split path into segments, filtering dangerous components
 * C9 fix: Validate both string and array paths to prevent path traversal
 * @param {string | Array<string>} path
 * @returns {Array<string>}
 */
export const splitPath = path => {
  const segments = typeof path === 'string' ? path.split('/') : path
  // Filter out empty segments, '.' (current dir), and '..' (parent dir) to prevent path traversal
  return segments.filter(p => p && p !== '.' && p !== '..')
}

/**
 * 
 * @param {string} url 
 * @returns {PathInfo}
 */
export function extractPathInfo(url) {
  let idx = url.lastIndexOf('/')
  const filename = url.substring(idx + 1)
  idx = filename.lastIndexOf('.')
  const ext = filename.substring(idx + 1)
  return { url, filename, ext }
}

/**
 * @param {string} path
 * @param {SwHandler} sw
 * @returns {Promise<FSFileEntry | undefined>}
 */
export const getFile = async (path, sw) => {
  const arr = splitPath(path)
  return await findFileInRoots(sw.roots, arr)
}

/**
 * @param {string} path
 * @param {SwHandler} sw
 * @returns {Promise<ArrayBuffer | undefined>}
 */
export const getFileContent = async (path, sw) => {
  const match = await getFile(path, sw)
  if (match) {
    fileIsRequested(path, match, sw)
    return readAsArrayBuffer(match)
  }
}

/**
 * @param {FSDirectoryEntry} dir
 * @returns {Promise<Array<FSEntry>>}
 */
export const readDir = async dir => {
  const out = []
  for await (const fileHandles of dir.handle.values()) {
    out.push(toFSEntry(fileHandles, dir))
  }
  return out
}

/** add path to cache, with content that can be anything that response allows
 * https://developer.mozilla.org/en-US/docs/Web/API/Response/Response
 *
 *
 * @param {Cache} cache
 * @param {string} path
 * @param {Blob|ArrayBuffer|TypedArray|DataView|FormData|ReadableStream|URLSearchParams|string} content
 * @returns {Promise<void>}
 */
export const addToCache = async (cache, path, content) => await cache.put(new Request(path), new Response(content))


/**
 * @param {SwHandler} sw 
 * @param {Array<string>} paths 
 * @param {boolean} ignoreMissing 
 * @returns {Promise<[string, FSFileEntry | undefined][]>}
 */
export const addPreLoadAll = async (sw, paths, ignoreMissing) => {
  /** @type {[string, FSFileEntry | undefined][]}*/
  const out = []
  for (const path of paths) {
    //todo load parallel
    const match = await addPreLoad(sw, path, ignoreMissing)
    out.push([path, match])
  }
  return out
}

/**
 * @param {SwHandler} sw 
 * @param {string} path 
 * @param {boolean} ignoreMissing 
 * @returns {Promise<FSFileEntry | undefined>}
 */
export const addPreLoad = async (sw, path, ignoreMissing) => {
  const match = await findFileInRoots(sw.roots, path)
  if (!match) {
    if (!ignoreMissing) throw new Error('File not found ' + path)
    return
  }
  const f = await match.handle.getFile()
  match.lastModified = f.lastModified
  await addToCache(sw.cache, path, await readAsArrayBuffer(f))
  return match
}

/**
 *
 * @param {*} workerScript
 * @param {*} _getFile
 * @param {*} param2
 * @returns {Promise<SwHandler>}
 */
export const registerServiceWorker = async (
  workerScript,
  _getFile = getFileContent,
  { prefix = '/swfs/', scope = '/' } = {},
) => {
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      if (!reg) { // no sw register yet, go for it
        await navigator.serviceWorker.register(workerScript, { scope, })
      } else if (!navigator.serviceWorker.controller) {
        // handling hard refresh here
        await new Promise((resolve) => {
          const messageChannel = new MessageChannel()
          messageChannel.port1.onmessage = (event) => resolve(event.data)
          reg.active.postMessage({ type: 'CLAIM_CLIENTS' }, [messageChannel.port2])
        })
      }
    } catch (error) {
      console.error(`service worker registration failed with ${error}`)
      throw error
    }

    // this code handle app first load, when the serviceWorker is still activating
    // here we force it to wait until it switch to 'ativated' state
    const rdy = await navigator.serviceWorker.ready
    if (rdy.active.state != 'activated') {
      await new Promise((resolve) => {
        const listener = () => {
          rdy.active.removeEventListener('statechange', listener)
          resolve()
        }
        rdy.active.addEventListener('statechange', listener)
      })
    }

    /** @type {SwHandler} */
    const sw = { roots: [] }
    sw.api = messageProxy(navigator.serviceWorker, {
      getFile: async ({ path }) => {
        const file = await _getFile(path, sw)
        if (file) {
          await addToCache(sw.cache, path, file)
          return 'ok'
        } else {
          return 'not_found'
        }
      },
    })

    // id is important as we use it to name the temporary cache instance
    // for now we use fetch to extract our id, but a better way could be found later
    const id = await fetch(prefix + 'init').then(res => {
      if (!res.ok) {
        console.error(res)
        throw new Error('failed to start service worker')
      }
      return res.text()
    })
    sw.id = id
    const cacheId = prefix + id
    window.addEventListener('beforeunload', () => caches.delete(cacheId))

    sw.cache = await caches.open(cacheId)
    sw.defProjectName = 'project'
    sw.filesToCheck = []
    sw.lastCheck = Date.now()
    sw.base = new URL(`${prefix}${sw.id}/`, document.baseURI).toString()
    checkFiles(sw)

    return sw
  } else {
    throw new Error('service worker unavailable')
  }
}

/**
 * @param {SwHandler} sw 
 */
export const clearFs = async sw => {
  stopCheckFiles(sw) // C8 fix: Stop the file checking loop when clearing FS
  sw.roots = []
  await clearCache(sw.cache)
}

/**
 * @param {Cache} cache 
 */
export const clearCache = async cache => {
  const keys = await cache.keys()
  await Promise.all(keys.map(key => cache.delete(key)))
}

/**
 *
 * @param {DataTransfer} dt
 * @returns {Promise<Array<FSEntry>>}
 */
export const extractEntries = async dt => {
  const items = dt.items
  if (!items) return []

  const root = {
    name: '',
    fsDir: '',
    fullPath: '',
  }

  /** @type {Array<FSEntry>} */
  const fsEntries = []
  // Use DataTransferItemList interface to access the items(s).
  // todo remove usage of FileEntry webkitGetAsEntry and use new File_System_API
  // todo load files parallel

  // DataTransferItemList is not an array by default
  for (const item of Array.from(items)) {
    // If dropped items aren't files or directories, reject them
    // Directories also have kind === 'file'
    if (item.kind === 'file') {
      let handle  // L9 fix: Use let instead of var
      try {
        handle = await item.getAsFileSystemHandle()
      }
      catch {
        handle = await safariGetAsHandle(item)
      }
      const fsEntry = toFSEntry(handle, root)
      fsEntries.push(fsEntry)
    }
  }
  return fsEntries
}

/**
 * 
 * @param {Array<Array<FSEntry>>} roots 
 * @param {Array<string> | string} path 
 * @returns {Promise<FSFileEntry | undefined>}
 */
export const findFileInRoots = async (roots, path) => {
  const paths = splitPath(path)
  for (const root of roots) {
    const out = await findFile(root, paths, 0)
    if (out) return out
  }
}

/**
 * Finds a file by path
 * @param {Array<FSEntry>} arr 
 * @param {Array<string>} path 
 * @param {number} i The current position in the path
 * @returns {Promise<FSFileEntry | undefined>}
 */
export const findFile = async (arr, path, i) => {
  const name = path[i]
  const match = arr.find(f => f.name === name)
  if (match) {
    if (i >= path.length - 1) {
      if (match.isFile) {
        return match
      } else {
        return undefined
      }
    }

    if (match.isDirectory) {
      const children = await loadDir(match)
      return findFile(children, path, i + 1)
    } else {
      return undefined
    }
  }
}

/**
 * @param {FSDirectoryEntry} dir 
 * @returns {Promise<Array<FSEntry>>}
 */
export const loadDir = async dir => {
  if (dir.isDirectory && dir.children === undefined) {
    dir.children = await readDir(dir)
  }
  return dir.children ?? []
}

/**
 * Stop the file checking loop
 * C8 fix: Add cancellation mechanism for the requestAnimationFrame loop
 * @param {SwHandler} sw
 */
export const stopCheckFiles = sw => {
  if (sw._checkFilesRafId) {
    cancelAnimationFrame(sw._checkFilesRafId)
    sw._checkFilesRafId = undefined
  }
}

/**
 * This function is async but it is intentionally called without await
 * C8 fix: Track rAF ID for cancellation
 * @param {SwHandler} sw
 */
export const checkFiles = async sw => {
  try {
    const now = Date.now()
    if (now - sw.lastCheck > 300 && sw.filesToCheck.length !== 0) {
      sw.lastCheck = now
      // H8 fix: Snapshot the array before async operations to prevent race conditions
      // if fileDropped() clears filesToCheck during the await
      const filesToCheckSnapshot = [...sw.filesToCheck]
      // L6 fix: Use Promise.allSettled so one file error doesn't stop all checking
      const results = await Promise.allSettled(filesToCheckSnapshot.map(entryCheckPromise))
      let filesToCheck = results
        .filter(r => r.status === 'fulfilled')
        .map(r => r.value)
      filesToCheck = filesToCheck.filter(([entry, _file]) => entry.lastModified !== entry._lastModified)
      if (filesToCheck.length) {
        const addToCachePromises = filesToCheck.map(([entry, file]) => addToCache(sw.cache, entry.fullPath, file))
        const files = filesToCheck.map(([entry, _file]) => entry.fullPath)
        await Promise.all(addToCachePromises)//All files must be added to cache
        sw.onfileschange?.(files)
      }

      // TODO clear sw cache
    }
  } catch (error) {
    console.error('Error checking files for changes:', error)
  }
  // Store the rAF ID so it can be cancelled
  // H26 fix: Catch unhandled promise rejections in recursive call
  sw._checkFilesRafId = requestAnimationFrame(() => checkFiles(sw).catch(err => {
    console.error('Unhandled error in file change detection:', err)
  }))
}

/**
 * A single dropped file runs itself. A dropped folder or set of files runs the
 * file `pickEntry` names from the top-level file names, and analyzeProject asks
 * it again with package.json's text; without `pickEntry`, index.js or
 * package.json `main`.
 * @param {SwHandler} sw
 * @param {Array<FSEntry>} files
 * @param {PickEntry} [pickEntry]
 */
export async function fileDropped(sw, files, pickEntry) {
  sw.filesToCheck.length = 0
  sw.fileToRun = 'index.js'
  sw.pickEntry = undefined
  await clearFs(sw)
  /** @type {Array<FSEntry>}*/
  let rootFiles = []
  if (files.length === 1 && !files[0].isDirectory) {
    rootFiles.push(files[0])
    sw.fileToRun = files[0].name
  } else {
    let folder
    if (files.length === 1) {
      const file = files[0]
      folder = sw.folderName = file.name
      file.fullPath = ''
      rootFiles = await readDir(file)
    } else {
      rootFiles = Array.from(files)
    }
    if (pickEntry) {
      sw.pickEntry = pickEntry
      sw.entryNames = rootFiles.filter(f => f.isFile).map(f => f.name)
      sw.entryFolder = folder
      sw.fileToRun = pickEntry(sw.entryNames, { folder }) ?? sw.fileToRun
    }
  }
  sw.roots.push(rootFiles)
}

/**
 * @param {SwHandler} sw 
 */
export async function analyzeProject(sw) {
  const alias = await getWorkspaceAliases(sw)

  if (sw.fileToRun && sw.fileToRun[0] != '/') {
    sw.fileToRun = `/${sw.fileToRun}`
  }

  const preLoad = [sw.fileToRun, '/package.json']
  const _loaded = await addPreLoadAll(sw, preLoad, true)

  sw.projectName = sw.defProjectName
  if (sw.folderName) {
    sw.projectName = sw.folderName
  } else if (sw.fileToRun !== 'index.js') {
    sw.projectName = sw.fileToRun.replace(/\.js$/, '')
  }

  let script = ''
  if (sw.fileToRun) {
    const file = await findFileInRoots(sw.roots, sw.fileToRun)
    if (file) {
      sw.filesToCheck.push(file)
      script = await readAsText(file)
    } else {
      throw new Error(`main file not found ${sw.fileToRun}`)
    }
  }
  return { alias, script }
}

/**
 * Parse package.json, and return a list of workspace aliases.
 * Also parses the main file to run, if any.
 *
 * @param {SwHandler} sw
 * @returns {Promise<Array<WorkspaceAlias>>}
 */
/**
 * Sanitize a path to prevent path traversal attacks
 * @param {string} path
 * @returns {string}
 */
const sanitizePath = path => {
  if (!path || typeof path !== 'string') return ''
  // Remove .. segments and normalize path
  return path.split('/').filter(p => p && p !== '.' && p !== '..').join('/')
}

const getWorkspaceAliases = async sw => {
  /** @type {Array<WorkspaceAlias>} */
  const alias = []
  const pkgFile = await findFileInRoots(sw.roots, 'package.json')
  if (pkgFile) {
    try {
      sw.filesToCheck.push(pkgFile)
      const text = await readAsText(pkgFile)
      const pack = JSON.parse(text)
      if (sw.pickEntry) sw.fileToRun = sw.pickEntry(sw.entryNames ?? [], { folder: sw.entryFolder, packageJson: text }) ?? sw.fileToRun
      else if (pack.main) sw.fileToRun = sanitizePath(pack.main)
      if (pack.workspaces)
        for (const workspace of pack.workspaces) {
          // H10 fix: Sanitize workspace path early to prevent path traversal
          const sanitizedWorkspace = sanitizePath(workspace)
          if (!sanitizedWorkspace) continue // Skip invalid workspace paths
          const workspacePackageFile = await findFileInRoots(sw.roots, `/${sanitizedWorkspace}/package.json`)
          let workspacePackageJson
          if (workspacePackageFile) workspacePackageJson = JSON.parse(await readAsText(workspacePackageFile))
          const name = workspacePackageJson?.name ?? sanitizedWorkspace
          const main = sanitizePath(workspacePackageJson?.main ?? 'index.js')
          alias.push({ name, path: `/${sanitizedWorkspace}/${main}` })
        }
    } catch (error) {
      error.message = `failed to parse package.json\n  ${error}`
      throw error
    }
  }
  return alias
}

/**
 * @param {Array<FSFileEntry>} arr 
 * @param {FSFileEntry | string} file 
 * @returns 
 */
export const findByFullPath = (arr, file) => {
  const path = typeof file === 'string' ? file : file.fullPath
  return arr.find(f => f.fullPath === path)
}

/**
 * @param {string} path 
 * @param {FSFileEntry} file 
 * @param {SwHandler} sw 
 */
export const fileIsRequested = (path, file, sw) => {
  const match = findByFullPath(sw.filesToCheck, file)
  if (match) return
  sw.filesToCheck.push(file)
}
